import moment from 'moment';
import api from './api';

const NOTE_DATE_FORMAT = 'MM/DD/YYYY - h:mm A';

const apiWithTags = api.enhanceEndpoints({
	addTagTypes: ['ManagementCompanyWorkforce', 'WorkforceNotes']
});

const managementCompanyWorkforceApi = apiWithTags.injectEndpoints({
	tagTypes: ['ManagementCompanyWorkforce', 'WorkforceNotes'],
	endpoints: (build) => ({
		getManagementCompanyInfo: build.query({
			query: (managementCompanyId) => ({
				url: `/management-companies/${managementCompanyId}`,
				headers: {
					'management-company-id': managementCompanyId
				}
			}),
			providesTags: ['ManagementCompanyWorkforce']
		}),
		getWorkforceByManagementCompany: build.query({
			query: (managementCompanyId) => ({
				url: `flex/management-companies/${managementCompanyId}/workforce`,
				headers: {
					'management-company-id': managementCompanyId
				}
			}),
			providesTags: ['ManagementCompanyWorkforce']
		}),
		getProfessional: build.query({
			query: ({ managementCompanyId, caregiverId }) => ({
				url: `/flex/management-companies/${managementCompanyId}/caregivers/${caregiverId}`,
				headers: {
					'management-company-id': managementCompanyId
				}
			}),
			providesTags: ['ManagementCompanyWorkforce']
		}),
		getUpcomingShiftsByProfessional: build.query({
			query: ({ managementCompanyId, caregiverId, queryParams = '' }) => ({
				url: `/flex/management-companies/${managementCompanyId}/caregivers/${caregiverId}/workforce/upcoming-shifts?${queryParams}`,
				headers: {
					'management-company-id': managementCompanyId
				}
			}),
			providesTags: ['ManagementCompanyWorkforce']
		}),
		updateWorkforceStatus: build.mutation({
			query: ({ managementCompanyId, caregiverId, body }) => ({
				url: `flex/management-companies/${managementCompanyId}/caregivers/${caregiverId}/workforce/status`,
				method: 'PUT',
				body,
				headers: {
					'management-company-id': managementCompanyId
				}
			}),
			invalidatesTags: ['ManagementCompanyWorkforce']
		}),
		getNoteCategories: build.query({
			query: ({ managementCompanyId }) => ({
				url: `/flex/management-companies/${managementCompanyId}/note-categories`,
				headers: {
					'management-company-id': managementCompanyId
				}
			}),
			providesTags: ['ManagementCompanyWorkforce']
		}),
		getNotes: build.query({
			query: ({ managementCompanyId, caregiverId }) => ({
				url: `/flex/management-companies/${managementCompanyId}/caregivers/${caregiverId}/notes`,
				headers: {
					'management-company-id': managementCompanyId
				}
			}),
			transformResponse: (notes) =>
				notes.map((note) => ({
					...note,
					formatted_creation_date: moment(note.creation_date).format(NOTE_DATE_FORMAT),
					formatted_last_updated_date: note.last_updated_date
						? moment(note.last_updated_date).format(NOTE_DATE_FORMAT)
						: null
				})),
			providesTags: ['WorkforceNotes']
		}),
		addNote: build.mutation({
			query: ({ managementCompanyId, caregiverId, body }) => ({
				url: `flex/management-companies/${managementCompanyId}/caregivers/${caregiverId}/notes`,
				method: 'POST',
				body,
				headers: {
					'management-company-id': managementCompanyId
				}
			}),
			invalidatesTags: ['WorkforceNotes']
		}),
		deleteNote: build.mutation({
			query: ({ managementCompanyId, caregiverId, noteId }) => ({
				url: `flex/management-companies/${managementCompanyId}/caregivers/${caregiverId}/notes/${noteId}`,
				method: 'DELETE',
				body: {},
				headers: {
					'management-company-id': managementCompanyId
				}
			}),
			invalidatesTags: (result, errors) => {
				if (result && !errors) {
					return ['WorkforceNotes'];
				}
				return [];
			}
		}),
		updateNote: build.mutation({
			query: ({ managementCompanyId, caregiverId, noteId, body }) => ({
				url: `flex/management-companies/${managementCompanyId}/caregivers/${caregiverId}/notes/${noteId}`,
				method: 'PUT',
				body,
				headers: {
					'management-company-id': managementCompanyId
				}
			}),
			invalidatesTags: (result, errors) => {
				if (result && !errors) {
					return ['WorkforceNotes'];
				}
				return [];
			}
		}),
		updateEmployeeIDs: build.mutation({
			query: ({ managementCompanyId, caregiverId, body }) => ({
				url: `flex/management-companies/${managementCompanyId}/caregivers/${caregiverId}/employee-ids`,
				method: 'PUT',
				body,
				headers: {
					'management-company-id': managementCompanyId
				}
			}),
			invalidatesTags: (result, errors) => {
				if (result && !errors) {
					return ['ManagementCompanyWorkforce'];
				}
				return [];
			}
		})
	})
});

export const {
	useGetManagementCompanyInfoQuery,
	useGetWorkforceByManagementCompanyQuery,
	useGetProfessionalQuery,
	useGetUpcomingShiftsByProfessionalQuery,
	useLazyGetUpcomingShiftsByProfessionalQuery,
	useUpdateWorkforceStatusMutation,
	useGetNotesQuery,
	useAddNoteMutation,
	useDeleteNoteMutation,
	useGetNoteCategoriesQuery,
	useUpdateEmployeeIDsMutation,
	useUpdateNoteMutation
} = managementCompanyWorkforceApi;
